import request from "./request";
import type {
  ResolvePendingCertificateBody,
  UploadCert,
  UserCoursesUploadCert,
  UserUploadCert,
  UsersUploadCert,
} from "../shared/api-types/certificate";

export const getPendingCertificates = async (crewId: number, baseIds?: number[] | null) => {
  const params = new URLSearchParams();
  baseIds?.forEach((id) => params.append("baseIds", String(id)));
  const queryString = params.toString();
  const url = `/certificate/pending/${crewId}${queryString ? `?${queryString}` : ""}`;
  const json = await request(url);
  return json;
};

export const getPendingCertificatesCount = async (crewId: number, baseIds?: number[] | null) => {
  const params = new URLSearchParams();
  baseIds?.forEach((id) => params.append("baseIds", String(id)));
  const queryString = params.toString();
  const url = `/certificate/pending/${crewId}/count${queryString ? `?${queryString}` : ""}`;
  const json = await request(url);
  return json;
};

/** Approve or decline a pending certificate; declining needs a reason (CREW-577). */
export const resolvePending = async (
  crewId: number,
  objectId: number,
  status: ResolvePendingCertificateBody["status"],
  isCustom: boolean,
  declineReason?: string,
) => {
  const json = await request.put(`/certificate/pending/${crewId}/resolve/${objectId}`, {
    status,
    isCustom,
    ...(declineReason ? { declineReason } : {}),
  });
  return json;
};

const buildBaseCertFormData = ({ crewId, courseId, completed, validUntil, comment }: UploadCert): FormData => {
  const formData = new FormData();
  formData.append("crewId", String(crewId));
  formData.append("courseId", String(courseId));
  formData.append("completed", completed);
  validUntil && formData.append("validUntil", validUntil);
  comment && formData.append("comment", comment);
  return formData;
};

export const uploadCourseCertificate = async ({
  isCustom,
  files,
  ...payload
}: UserUploadCert & { isCustom: boolean; files: File[] }) => {
  const formData = buildBaseCertFormData(payload);
  files.forEach((file) => formData.append("file", file));
  formData.append("userId", String(payload.userId));
  const endpoint = isCustom ? "/certificate/custom" : "/certificate";
  return request(endpoint, "POST", formData);
};

export const bulkUploadCertificate = async ({
  isCustom,
  files,
  ...payload
}: UsersUploadCert & { isCustom: boolean; files: File[] }) => {
  const formData = buildBaseCertFormData(payload);
  formData.append("file", files[0]);
  payload.userIds.forEach((id) => formData.append("userIds", String(id)));
  const endpoint = isCustom ? "/certificate/custom/bulk" : "/certificate/bulk";
  return request(endpoint, "POST", formData);
};

export const bulkUploadUserCoursesCertificate = async ({
  isCustom,
  files,
  ...payload
}: UserCoursesUploadCert & { isCustom: boolean; files: File[] }) => {
  const formData = new FormData();
  formData.append("crewId", String(payload.crewId));
  formData.append("userId", String(payload.userId));
  formData.append("completed", payload.completed);
  payload.validUntil && formData.append("validUntil", payload.validUntil);
  payload.comment && formData.append("comment", payload.comment);
  formData.append("file", files[0]);
  payload.courseIds.forEach((id) => formData.append("courseIds", String(id)));
  const endpoint = isCustom ? "/certificate/custom/bulk/user-courses" : "/certificate/bulk/user-courses";
  return request(endpoint, "POST", formData);
};

export const updateCertificateDates = async (
  crewId: number,
  certificateId: number,
  isCustom: boolean,
  completedDate?: string | null,
  validPeriod?: string | null,
) => {
  const json = await request.put(`/certificate/pending/${crewId}/${certificateId}`, {
    completedDate,
    validPeriod,
    isCustom,
  });
  return json;
};
